import React, { useState, useEffect } from 'react';
import { CalendarIcon, XMarkIcon, PlayIcon, ClockIcon } from '@heroicons/react/24/outline';
import Button from '../ui/Button';
import { membershipsService } from '../../services/membershipsService';

interface ScheduledTierChange {
  id: string;
  currentTierId: string;
  newTierId: string;
  scheduledFor: string;
  reason?: string;
  status: 'PENDING' | 'COMPLETED' | 'CANCELLED';
  createdAt: string;
}

interface ScheduledTierChangesListProps {
  membershipId: string;
  onRefresh?: () => void;
}

const ScheduledTierChangesList: React.FC<ScheduledTierChangesListProps> = ({
  membershipId,
  onRefresh,
}) => {
  const [changes, setChanges] = useState<ScheduledTierChange[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const loadScheduledChanges = async () => {
    setIsLoading(true);
    setError('');
    try {
      const res = await membershipsService.listScheduledTierChanges(membershipId);
      setChanges(res.scheduledChanges || []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load scheduled changes');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadScheduledChanges();
  }, [membershipId]);

  const handleCancel = async (changeId: string) => {
    if (!confirm('Are you sure you want to cancel this scheduled tier change?')) {
      return;
    }

    setActionLoading(changeId);
    try {
      await membershipsService.cancelScheduledTierChange(membershipId, changeId);
      await loadScheduledChanges();
      onRefresh?.();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to cancel scheduled change');
    } finally {
      setActionLoading(null);
    }
  };

  const handleExecuteNow = async (changeId: string) => {
    if (!confirm('Are you sure you want to execute this tier change immediately? You will be charged/credited accordingly.')) {
      return;
    }

    setActionLoading(changeId);
    try {
      await membershipsService.executeScheduledTierChange(membershipId, changeId);
      await loadScheduledChanges();
      onRefresh?.();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to execute tier change');
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const pendingChanges = changes.filter(c => c.status === 'PENDING');

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-sm text-red-600">{error}</p>
        <Button
          variant="outline"
          size="sm"
          onClick={loadScheduledChanges}
          className="mt-2"
        >
          Retry
        </Button>
      </div>
    );
  }

  if (pendingChanges.length === 0) {
    return (
      <div className="text-center py-8 px-4">
        <CalendarIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
        <p className="text-gray-600 text-sm">No scheduled tier changes</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">
        Scheduled Tier Changes
      </h3>
      
      {pendingChanges.map((change) => (
        <div
          key={change.id}
          className="p-4 bg-blue-50 border border-blue-200 rounded-lg"
        >
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <ClockIcon className="w-5 h-5 text-blue-600" />
                <h4 className="font-medium text-gray-900">
                  Tier Change Scheduled
                </h4>
              </div>
              
              <div className="space-y-1 text-sm text-gray-700">
                <p>
                  <span className="font-medium">Effective Date:</span>{' '}
                  {formatDate(change.scheduledFor)}
                </p>
                {change.reason && (
                  <p>
                    <span className="font-medium">Reason:</span> {change.reason}
                  </p>
                )}
                <p className="text-xs text-gray-500 mt-2">
                  Scheduled on {formatDate(change.createdAt)}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 ml-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleExecuteNow(change.id)}
                disabled={actionLoading === change.id}
                leftIcon={<PlayIcon className="w-4 h-4" />}
                title="Execute this change immediately"
              >
                Execute Now
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleCancel(change.id)}
                disabled={actionLoading === change.id}
                leftIcon={<XMarkIcon className="w-4 h-4" />}
                title="Cancel this scheduled change"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ScheduledTierChangesList;
